import { Box, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';

interface StepStyleProps {
  active?: boolean;
  completed?: boolean;
}

/**
 * Everything trailing the title: the stepper, then the close button.
 *
 * The `marginLeft: auto` that used to sit on `StepContainer` lives here now,
 * because the two are no longer alternatives — a stepped header shows the dots AND
 * the ✕, so the push-right belongs to the pair rather than to whichever one won.
 *
 * Figma puts the ✕ glyph 24px from the dots; the ✕ button's own 4px padding makes up the rest.
 */
export const HeaderActions = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(2.5),
  marginLeft: 'auto',
}));

export const StepContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(0.5),
}));

export const StepIndicator = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'active' && prop !== 'completed',
})<StepStyleProps>(({ theme, active, completed }) => ({
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '8px',
  // Figma `stepper-new` (`27006:108440`): the current step a 24px pill, done and current
  // #49BACA, to-do #D6E0E2.
  backgroundColor: active || completed ? theme.palette.primary.main : theme.palette.grey[90],
  borderRadius: '4px',
  transition: 'all 0.3s ease',
  width: active ? '24px' : '8px',
  // No cursor of its own: the dot inherits the hand from a `StepButton` and the arrow
  // from a `StepWrapper`. It used to declare `default`, and with no label the dot is
  // all the pointer ever touches, so a reached dot never showed the hand.
}));

export const StepLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'active',
})<{ active?: boolean }>(({ theme, active }) => ({
  fontSize: '12px',
  fontWeight: active ? 600 : 400,
  color: active ? theme.palette.text.primary : theme.palette.text.secondary,
  marginRight: theme.spacing(0.5),
  whiteSpace: 'nowrap',
}));

export const StepWrapper = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  // An inert step keeps the arrow, over its label too, where `auto` would show a text caret.
  cursor: 'default',
});

/**
 * A step the run has already reached — the stepper as NAVIGATION, not just
 * orientation (import wizard spec §6.2.24: reached steps jump back to themselves;
 * forward movement stays exclusively through each step's own gate).
 *
 * A real `button`, not a Box with a click handler: an inert step must not be
 * focusable or announced as actionable, so the two cases are two elements rather
 * than one element with a `cursor` swap. The reset is deliberate — the indicator
 * inside carries the whole visual, and a button's own chrome would double it.
 */
export const StepButton = styled('button')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  padding: 0,
  border: 'none',
  background: 'transparent',
  cursor: 'pointer',
  // A dot is an 8px target, so the hit area is grown with padding-free spacing:
  // the wrapper stays visually tight while the pointer gets the row's full height.
  alignSelf: 'stretch',
  '&:focus-visible': {
    outline: `2px solid ${theme.palette.primary.main}`,
    outlineOffset: 2,
    borderRadius: 4,
  },
  // The dot itself brightens on hover, so the affordance reads without moving anything.
  '&:hover > *': { opacity: 0.7 },
}));

/**
 * Figma `card-header-upd`: 51px high, hairline included. The 12px block padding leaves
 * room for the 24px ✕; the min-height holds 51 for a header without one.
 */
export const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  minHeight: 51,
  padding: '12px 24px',
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

export const TitleContent = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  gap: 16,
  flex: 1,
});

export const StyledDialogContent = styled(DialogContent)({
  '&.MuiDialogContent-root': {
    padding: '24px',
  },
});

export const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
  padding: '16px 24px',
  borderTop: `1px solid ${theme.palette.divider}`,
}));
